import { useState, useEffect, useRef } from 'react';
import { Megaphone } from 'lucide-react';

// 定义广播数据类型
export interface BroadcastItem {
  id: string;
  company: string;
  contact: string;
  productName: string;
  amount: number;
  unit: string;
  time: string;
}

interface BroadcastTickerProps {
  items: BroadcastItem[];
  className?: string;
}

const BroadcastTicker = ({ items, className = '' }: BroadcastTickerProps) => {
  // 如果数据少于2条，不需要滚动效果，直接静态展示或隐藏
  if (!items || items.length < 2) {
    if (!items || items.length === 0) return null;
    return (
      <div className={`bg-gradient-to-r from-[#dce5ff] to-white border-gray-100 py-2 ${className}`}>
        <div className="section-container flex items-center gap-3 text-sm">
           <div className="flex items-center gap-1 text-[#1c5bff] flex-shrink-0">
            <Megaphone className="w-4 h-4" />
            <span className="font-medium">最新成交</span>
          </div>
          <div className="flex-1 text-gray-600 truncate">
             <span className="font-medium text-gray-900 mr-2">{items[0].company}</span>
             <span className="mr-2">{items[0].contact}</span>
             <span className="text-[#1c5bff] mr-2">{items[0].productName}</span>
             <span className="mr-2">{items[0].amount}{items[0].unit}</span>
             <span className="text-gray-400 text-xs">{items[0].time}</span>
          </div>
        </div>
      </div>
    );
  }

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [noTransition, setNoTransition] = useState(false);
  
  // 构建循环列表：原列表 + 原列表的第一项
  // 例如: [A, B, C] -> [A, B, C, A]
  const loopItems = [...items, items[0]];

  const itemHeight = 24; // h-6 = 1.5rem = 24px

  useEffect(() => {
    let timer: number | undefined;

    const startScroll = () => {
      timer = window.setInterval(() => {
        if (!isPaused) {
          setCurrentIndex((prev) => {
            const nextIndex = prev + 1;
            return nextIndex;
          });
        }
      }, 3000);
    };

    startScroll();

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isPaused]);

  // 监听 currentIndex 变化，处理无缝重置
  useEffect(() => {
    // 如果当前索引到达了复制的那一项（最后一项，索引等于原数组长度）
    if (currentIndex === items.length) {
      // 1. 禁用过渡动画，准备瞬移
      setNoTransition(true);
      
      // 2. 使用 setTimeout 确保在下一帧执行重置，此时用户看到的是副本项（视觉上等于第一项）
      setTimeout(() => {
        // 3. 瞬间重置回第0项
        setCurrentIndex(0);
        
        // 4. 强制重绘后恢复过渡动画
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setNoTransition(false);
          });
        });
      }, 500); // 这个时间应匹配 CSS transition duration，确保动画播放完再重置
    }
  }, [currentIndex, items.length]);

  return (
    <div 
        className={`bg-gradient-to-r from-[#dce5ff] to-white border-gray-100 py-2 overflow-hidden ${className}`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
    >
      <div className="section-container flex items-center gap-3 text-sm">
        <div className="flex items-center gap-1 text-[#1c5bff] flex-shrink-0">
          <Megaphone className="w-4 h-4" />
          <span className="font-medium">最新成交</span>
        </div>
        
        {/* 滚动内容容器 */}
        <div className="flex-1 relative h-6 overflow-hidden">
          <div 
            className={`absolute w-full ${noTransition ? '' : 'transition-transform duration-500 ease-in-out'}`}
            style={{ transform: `translateY(-${currentIndex * itemHeight}px)` }}
          >
            {loopItems.map((item, index) => (
              <div key={`${item.id}-${index}`} className="h-6 flex items-center whitespace-nowrap text-gray-600">
                <span className="font-medium text-gray-900 mr-10 ml-2">{item.company}</span>
                <span className="mr-10">{item.contact}</span>
                <span className="text-[#1c5bff] mr-2">{item.productName}</span>
                <span className="mr-10">{item.amount}{item.unit}</span>
                <span className="text-gray-400 text-xs">{item.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastTicker;